import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import confetti from 'canvas-confetti';
import { Container, Spinner } from 'reactstrap';
import { supabase } from '../lib/supabaseClient';
import './PleasePage.css';

function PleasePage() {
  const { s } = useParams();

  const [question, setQuestion] = useState(null);
  const [loading, setLoading] = useState(!!s);
  const [error, setError] = useState(null);
  const [noClickCount, setNoClickCount] = useState(0);
  const [noPosition, setNoPosition] = useState(null);
  const [saidYes, setSaidYes] = useState(false);
  const noButtonRef = useRef(null);

  useEffect(() => {
    if (!s) {
      setLoading(false);
      return;
    }

    let cancelled = false;

    async function fetchAsk() {
      const { data, error: fetchError } = await supabase
        .from('questions')
        .select('question')
        .eq('string', s)
        .maybeSingle();

      if (cancelled) return;

      if (fetchError) {
        setError(fetchError.message);
        setLoading(false);
        return;
      }

      setQuestion(data ? data.question : null);
      setLoading(false);
    }

    fetchAsk();
    return () => { cancelled = true; };
  }, [s]);

  const handleNoClick = () => {
    setNoClickCount((c) => c + 1);
  };

  const handleNoJump = useCallback(() => {
    if (noClickCount < 5) return;
    setNoPosition({
      x: Math.random() * (window.innerWidth - 100),
      y: Math.random() * (window.innerHeight - 50),
    });
  }, [noClickCount]);

  const handleYesClick = () => {
    setSaidYes(true);
    confetti({
      particleCount: 100,
      spread: 70,
      origin: { y: 0.6 },
      colors: ['#ff9ebb', '#ffb3c6', '#c9184a', '#2d6a4f', '#ffd6e0'],
    });
    confetti({
      particleCount: 50,
      angle: 60,
      spread: 55,
      origin: { x: 0 },
      colors: ['#ff9ebb', '#c9184a'],
    });
    confetti({
      particleCount: 50,
      angle: 120,
      spread: 55,
      origin: { x: 1 },
      colors: ['#ff9ebb', '#c9184a'],
    });
  };

  const isLastChance = noClickCount >= 4;
  const yesScale = isLastChance ? (noClickCount >= 5 ? 2.8 : 2.5) : 1 + noClickCount * 0.15;
  const noScale = Math.max(0.75, 1 - noClickCount * 0.08);
  const noIsRunning = noClickCount >= 5;

  useEffect(() => {
    if (noIsRunning && noPosition === null) {
      setNoPosition({
        x: Math.max(20, window.innerWidth - 120),
        y: Math.max(20, window.innerHeight - 70),
      });
    }
  }, [noIsRunning, noPosition]);

  useEffect(() => {
    if (!noIsRunning) return;
    const handleTouchMove = (e) => {
      if (!e.touches[0] || !noButtonRef.current) return;
      const touch = e.touches[0];
      const rect = noButtonRef.current.getBoundingClientRect();
      const btnCenterX = rect.left + rect.width / 2;
      const btnCenterY = rect.top + rect.height / 2;
      const dist = Math.hypot(touch.clientX - btnCenterX, touch.clientY - btnCenterY);
      if (dist < 120) {
        handleNoJump();
      }
    };
    document.addEventListener('touchmove', handleTouchMove, { passive: true });
    return () => document.removeEventListener('touchmove', handleTouchMove);
  }, [noIsRunning, handleNoJump]);

  const noButtonTexts = ['No', 'Still no', 'Absolutely not', 'This is harassment', 'Stop enlarging that button', 'Get back here!'];
  const noButtonText = noIsRunning ? noButtonTexts[5] : noButtonTexts[Math.min(noClickCount, 4)];

  const giphyIds = [
    'kWcEOHhVTKo30Yfw1h',       // Stage 0 - Happy/Confident: kawaii anime girl blushing
    'TCLGV11Q0ZtC0',       // Stage 1 - First Rejection: awkward smile / nervous
    'sXv0vaA4331Ti',       // Stage 2 - Second Rejection: Puss in Boots eyes / pleading
    'fFa05KbZowXiEIyRse',  // Stage 3 - Third Rejection: sad crying cat / pleading
    'LG6mzmfXC3V1S',       // Stage 4 - Fourth Rejection: ugly crying / desperation
    '901mxGLGQN2PyCQpoc',  // Stage 5 - Fifth Rejection: spiraling / unhinged chaos
    'l0MYt5jPR6QX5pnqM',  // Yes - celebration
  ];
  const gifStage = saidYes ? 6 : Math.min(noClickCount, 5);
  const gifSrc = `https://media.giphy.com/media/${giphyIds[gifStage]}/giphy.gif`;

  if (loading) {
    return (
      <div className="please-page">
        <Container className="py-5 text-center">
          <Spinner color="primary" />
          <p className="mt-2 text-muted">Loading...</p>
        </Container>
      </div>
    );
  }

  if (error || !question) {
    return (
      <div className="please-page">
        <Container className="py-5">
          {error && <p className="text-danger">Something went wrong: {error}</p>}
          {!error && (
            <p className="text-muted">
              This link doesn&apos;t have a question, or it may be broken. Get your own ask from the home page.
            </p>
          )}
          <Link to="/">Your turn to maniplualate them back</Link>

        </Container>
      </div>
    );
  }

  return (
    <div className="please-page">
      <Container>
        <h1 className="question-text">
          {question}
        </h1>

        <div className="gif-container">
          <img src={gifSrc} alt="" className="please-gif" />
        </div>

        <div className="buttons-row">
          {saidYes ? (
            <div>
              <p className="lead mb-2" style={{ color: '#c9184a', fontSize: '1.5rem' }}>I knew you would say yes 🥰</p>
              <p className="text-muted mb-0" style={{ fontSize: '1rem' }}>This is the best decision you&apos;ve made all day.</p>
            </div>
          ) : (
            <>
              <button
                type="button"
                className="btn-yes"
                style={{
                  transform: `scale(${yesScale})`,
                  transformOrigin: isLastChance ? 'bottom center' : 'center',
                }}
                onClick={handleYesClick}
              >
                Yes
              </button>

              {noIsRunning ? (
                <button
                  ref={noButtonRef}
                  type="button"
                  className="btn-no running"
                  style={{
                    transform: `scale(${noScale})`,
                    left: noPosition?.x ?? Math.max(20, window.innerWidth - 120),
                    top: noPosition?.y ?? Math.max(20, window.innerHeight - 70),
                  }}
                  onMouseEnter={handleNoJump}
                  onTouchStart={handleNoJump}
                  onClick={handleNoClick}
                >
                  {noButtonText}
                </button>
              ) : (
                <button
                  type="button"
                  className="btn-no"
                  style={{ transform: `scale(${noScale})` }}
                  onClick={handleNoClick}
                >
                  {noButtonText}
                </button>
              )}
            </>
          )}
        </div>

        {saidYes && (
          <>
            <div className="nav-links">
              <Link to="/">Your turn to manipulate them back</Link>
            </div>
            <p className="text-muted mt-2 mb-0" style={{ fontSize: '12px' }}>No relationships were harmed in the making of this decision.</p>
          </>
        )}
      </Container>
    </div>
  );
}

export default PleasePage;
